import React, { useState, useEffect } from "react";
import "../../styles/requests-nav.css";
import { getActiveNavKey, getVisibleGroups } from "./nav-config";
import NavGroups from "./nav-groups";

const DESKTOP_QUERY = "(min-width: 1200px)";
const STORAGE_KEY = "requests.sidebarExpanded";

const readStoredExpanded = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
};

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (e) => setIsDesktop(e.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
};

function RequestSideBar({
  pagePermissions,
  permissionsLoaded,
  viewingPage,
  onNewRequest,
  onShowShortcuts,
}) {
  const isDesktop = useIsDesktop();
  // Desktop remembers the user's choice; smaller screens always start as a
  // rail and open as an overlay so the page content never gets squeezed.
  const [desktopExpanded, setDesktopExpanded] = useState(readStoredExpanded);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const expanded = isDesktop ? desktopExpanded : overlayOpen;
  const isOverlay = !isDesktop && overlayOpen;
  const activeKey = getActiveNavKey(viewingPage);
  const groups = getVisibleGroups(pagePermissions);

  useEffect(() => {
    setOverlayOpen(false);
  }, [viewingPage, isDesktop]);

  useEffect(() => {
    if (!isOverlay) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOverlayOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOverlay]);

  const handleToggle = () => {
    if (!isDesktop) return setOverlayOpen((prevState) => !prevState);

    setDesktopExpanded((prevState) => {
      try {
        localStorage.setItem(STORAGE_KEY, String(!prevState));
      } catch {}
      return !prevState;
    });
  };

  const handleNewRequest = () => {
    setOverlayOpen(false);
    onNewRequest();
  };

  return (
    <div className={`rq-nav-slot ${expanded ? "rq-nav-slot--expanded" : ""}`}>
      {isOverlay && (
        <div
          className="rq-nav__backdrop"
          onClick={() => setOverlayOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        id="requests-nav"
        className={`rq-nav ${expanded ? "rq-nav--expanded" : ""} ${
          isOverlay ? "rq-nav--overlay" : ""
        }`}
        aria-label="Requests navigation"
      >
        <div className="rq-nav__head">
          <span className="rq-nav__title">Requests</span>
          <button
            type="button"
            className="rq-nav__toggle"
            onClick={handleToggle}
            aria-expanded={expanded}
            aria-controls="requests-nav"
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <i
              className={`fa ${expanded ? "fa-angle-double-left" : "fa-bars"}`}
              aria-hidden="true"
            ></i>
          </button>
        </div>

        <button
          type="button"
          className="rq-nav__new"
          onClick={handleNewRequest}
          data-tooltip="New request"
        >
          <i className="fa fa-plus" aria-hidden="true"></i>
          <span className="rq-nav__text">New request</span>
        </button>

        <nav className="rq-nav__body">
          <NavGroups
            groups={groups}
            activeKey={activeKey}
            permissionsLoaded={permissionsLoaded}
          />
        </nav>

        <button
          type="button"
          className="rq-nav__footer-btn"
          onClick={onShowShortcuts}
          data-tooltip="Keyboard shortcuts (?)"
        >
          <i className="fa fa-keyboard-o" aria-hidden="true"></i>
          <span className="rq-nav__text">Shortcuts</span>
          <kbd className="rq-nav__kbd" aria-hidden="true">?</kbd>
        </button>
      </aside>
    </div>
  );
}

export default RequestSideBar;
